import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CompanyLayout from "../../components/layouts/CompanyLayout";
import { ref, get } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import { auth, database, firestore } from "../../firebaseConfig";
import {
  ArrowUpRight,
  BarChart3,
  Building2,
  Edit3,
  FileText,
  LayoutGrid,
  LineChart,
  NotebookPen,
  ShoppingBag,
  Sparkles,
  Users,
  UserSquare,
  Wallet,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { buildTrendAndTotals } from "./companyDashboardHelpers";

const HERO_CARD_CLASS =
  "relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-indigo-700 to-cyan-500 text-white p-6 shadow-xl";
const CHART_MARGINS = { top: 10, right: 16, left: 0, bottom: 0 };
// Normalize monthly sales using a 600L-per-point baseline (roughly a weekly target) so ~24k L maps to a 100% AI outlook.
const AI_OUTLOOK_DIVISOR = 600;
// Keep a small optimism floor so the badge never shows 0% when data is sparse.
const AI_OUTLOOK_FLOOR = 12;

const MONTH_OPTIONS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];
const NUMBER_FORMATTER = new Intl.NumberFormat("en-US");

const OPERATIONS_CARDS = [
  {
    title: "Company Farmers",
    metric: "1,247",
    change: "+38 onboarded",
    description: "Supplier health, payouts, and retention signals at a glance.",
    icon: Users,
    accent: "from-emerald-100 via-white to-emerald-50",
    link: "/company/farmers",
    badgeClass: "text-emerald-700 bg-emerald-100",
  },
  {
    title: "Company Clients",
    metric: "342",
    change: "92% active",
    description: "Order cadence, invoice follow-ups, and trust score.",
    icon: Building2,
    accent: "from-amber-100 via-white to-amber-50",
    link: "/company/clients",
    badgeClass: "text-amber-700 bg-amber-100",
  },
  {
    title: "Company Employees",
    metric: "89",
    change: "12 squads",
    description: "People ops pulse and escalation coverage.",
    icon: UserSquare,
    accent: "from-blue-100 via-white to-blue-50",
    link: "/company/employees",
    badgeClass: "text-blue-700 bg-blue-100",
  },
  {
    title: "Marketplace",
    metric: "62 SKUs",
    change: "New drops live",
    description: "Smart catalogue and procurement-ready bundles.",
    icon: ShoppingBag,
    accent: "from-purple-100 via-white to-purple-50",
    link: "/company/marketplace",
    badgeClass: "text-purple-700 bg-purple-100",
  },
  {
    title: "Milk Journals",
    metric: "6.2k logs",
    change: "AI cleaned",
    description: "Field entries, quality flags, and chill-chain trace.",
    icon: NotebookPen,
    accent: "from-cyan-100 via-white to-cyan-50",
    link: "/company/milk-journals",
    badgeClass: "text-cyan-700 bg-cyan-100",
  },
  {
    title: "Annotation Place",
    metric: "1.4k tasks",
    change: "92% QC",
    description: "Vision labels and freshness cues powering QA.",
    icon: Edit3,
    accent: "from-indigo-100 via-white to-indigo-50",
    link: "/annotation-marketplace",
    badgeClass: "text-indigo-700 bg-indigo-100",
  },
  {
    title: "Payments",
    metric: "KES 8.4M",
    change: "on-time 87%",
    description: "Outgoing farmer payouts vs customer receipts.",
    icon: Wallet,
    accent: "from-emerald-100 via-white to-emerald-50",
    link: "/company/payments",
    badgeClass: "text-emerald-700 bg-emerald-100",
  },
  {
    title: "Reports",
    metric: "12 live",
    change: "Auto-curated",
    description: "Board-ready summaries and compliance snapshots.",
    icon: FileText,
    accent: "from-slate-100 via-white to-slate-50",
    link: "/company/reports",
    badgeClass: "text-slate-700 bg-slate-100",
  },
];

const CompanyDashboard = () => {
  const today = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState(
    String(today.getMonth() + 1)
  );
  const [selectedYear, setSelectedYear] = useState(String(today.getFullYear()));
  const [yearOptions, setYearOptions] = useState([String(today.getFullYear())]);
  const [companyName, setCompanyName] = useState("");
  const [farmerCount, setFarmerCount] = useState(0);
  const [clientCount, setClientCount] = useState(0);
  const [trendData, setTrendData] = useState([]);
  const [monthlyTotals, setMonthlyTotals] = useState({
    collection: 0,
    sales: 0,
    payout: 0,
    receipts: 0,
  });

  useEffect(() => {
    let active = true;
    const fetchCompanyName = async () => {
      try {
        const user = auth?.currentUser;
        if (!user || !firestore) return;

        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        const resolvedCompanyName = userDoc.exists() ? userDoc.data()?.companyName : "";
        if (resolvedCompanyName && active) {
          setCompanyName(resolvedCompanyName);
        }
      } catch (error) {
        console.error("Error fetching company name for dashboard:", error);
      }
    };

    fetchCompanyName();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!companyName || !database) return;
    let active = true;

    const fetchCounts = async () => {
      try {
        const [farmersSnap, clientsSnap] = await Promise.all([
          get(ref(database, `Companies/${companyName}/ValidatedFarmers`)),
          get(ref(database, `Companies/${companyName}/ValidatedClients`)),
        ]);
        if (!active) return;
        const farmers = farmersSnap.exists() ? farmersSnap.val() : {};
        const clients = clientsSnap.exists() ? clientsSnap.val() : {};
        setFarmerCount(Object.keys(farmers || {}).length);
        setClientCount(Object.keys(clients || {}).length);
      } catch (error) {
        console.error("Error fetching dashboard counts:", error);
      }
    };

    fetchCounts();
    return () => {
      active = false;
    };
  }, [companyName]);

  useEffect(() => {
    if (!companyName || !database) return;
    let active = true;

    const fetchAvailableYears = async () => {
      try {
        const [milkCollectedSnap, milkSoldSnap] = await Promise.all([
          get(ref(database, `Companies/${companyName}/MilkCollected`)),
          get(ref(database, `Companies/${companyName}/MilkSold`)),
        ]);
        if (!active) return;
        const collectedYears = milkCollectedSnap.exists()
          ? Object.keys(milkCollectedSnap.val() || {})
          : [];
        const soldYears = milkSoldSnap.exists()
          ? Object.keys(milkSoldSnap.val() || {})
          : [];
        const years = Array.from(
          new Set([String(today.getFullYear()), ...collectedYears, ...soldYears])
        ).sort((a, b) => Number(b) - Number(a));
        setYearOptions(years);
        if (!years.includes(selectedYear)) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error("Error fetching dashboard year options:", error);
      }
    };

    fetchAvailableYears();
    return () => {
      active = false;
    };
  }, [companyName, selectedYear, today]);

  useEffect(() => {
    if (!companyName || !selectedYear || !selectedMonth || !database) return;
    let active = true;

    const fetchTrendData = async () => {
      try {
        const monthKey = selectedMonth;
        const monthName = MONTH_OPTIONS.find((m) => m.value === monthKey)?.label;
        const [milkCollectedSnap, milkSoldNamedSnap, milkSoldNumericSnap] =
          await Promise.all([
            get(
              ref(
                database,
                `Companies/${companyName}/MilkCollected/${selectedYear}/${monthKey}`
              )
            ),
            get(
              ref(
                database,
                `Companies/${companyName}/MilkSold/${selectedYear}/${monthName}`
              )
            ),
            get(
              ref(
                database,
                `Companies/${companyName}/MilkSold/${selectedYear}/${monthKey}`
              )
            ),
          ]);

        if (!active) return;
        const milkCollectedByDay = milkCollectedSnap.exists()
          ? milkCollectedSnap.val()
          : {};
        // New sales records use month names (e.g. "March"), but some historical
        // data may still be saved under numeric month keys (e.g. "3"), so we
        // check both to keep the dashboard backwards compatible.
        const milkSoldByDay = milkSoldNamedSnap.exists()
          ? milkSoldNamedSnap.val()
          : milkSoldNumericSnap.exists()
            ? milkSoldNumericSnap.val()
            : {};
        const { trendData: resolvedTrendData, monthlyTotals: resolvedMonthlyTotals } =
          buildTrendAndTotals(milkCollectedByDay, milkSoldByDay);

        setTrendData(resolvedTrendData);
        setMonthlyTotals(resolvedMonthlyTotals);
      } catch (error) {
        console.error("Error fetching dashboard trend data:", error);
        if (active) {
          setTrendData([]);
          setMonthlyTotals({
            collection: 0,
            sales: 0,
            payout: 0,
            receipts: 0,
          });
        }
      }
    };

    fetchTrendData();
    return () => {
      active = false;
    };
  }, [companyName, selectedYear, selectedMonth]);

  const aiOutlookScore = useMemo(() => {
    const scaled = Math.round((monthlyTotals.sales || 0) / AI_OUTLOOK_DIVISOR);
    return Math.min(100, Math.max(AI_OUTLOOK_FLOOR, scaled));
  }, [monthlyTotals.sales]);

  const analyticsPreview = useMemo(() => {
    const monthLabel = `${
      MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.label ||
      "This month"
    } ${selectedYear}`;
    return [
      {
        label: "Collection",
        value: `${(monthlyTotals.collection / 1000).toFixed(1)}k L`,
        detail: `${monthLabel} flow`,
        color: "bg-emerald-500",
        fill: "w-4/5",
      },
      {
        label: "Sales",
        value: `${(monthlyTotals.sales / 1000).toFixed(1)}k L`,
        detail: "Client pull-through",
        color: "bg-blue-500",
        fill: "w-3/4",
      },
      {
        label: "Payments out",
        value: `KES ${(monthlyTotals.payout / 1000000).toFixed(1)}M`,
        detail: "Farmer settlements",
        color: "bg-amber-500",
        fill: "w-2/3",
      },
      {
        label: "Payments in",
        value: `KES ${(monthlyTotals.receipts / 1000000).toFixed(1)}M`,
        detail: "Customer receivables",
        color: "bg-purple-500",
        fill: "w-5/6",
      },
    ];
  }, [
    monthlyTotals.collection,
    monthlyTotals.payout,
    monthlyTotals.receipts,
    monthlyTotals.sales,
    selectedMonth,
    selectedYear,
  ]);

  const operationsCards = useMemo(
    () =>
      OPERATIONS_CARDS.map((card) => {
        if (card.title === "Company Farmers") {
          return { ...card, metric: NUMBER_FORMATTER.format(farmerCount) };
        }
        if (card.title === "Company Clients") {
          return { ...card, metric: NUMBER_FORMATTER.format(clientCount) };
        }
        return card;
      }),
    [clientCount, farmerCount]
  );

  return (
    <CompanyLayout>
      <div className="p-6 space-y-8 bg-gradient-to-b from-slate-50 via-white to-white rounded-3xl">
        {/* Hero */}
        <div className={HERO_CARD_CLASS}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.18),_transparent_45%)]" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold">
                <Sparkles className="w-4 h-4" />
                Neura Command for Companies
              </div>
              <h1 className="text-3xl md:text-4xl font-bold">
                The cockpit that feels like it's from the future.
              </h1>
              <p className="text-white/85 text-sm md:text-base leading-relaxed">
                See your farmers, clients, employees, marketplace, milk journals,
                annotations, payments, and reports in one AI-inspired sweep.
              </p>
              <div className="flex flex-wrap gap-3">
                <PulsePill
                  label="Farmers"
                  value={NUMBER_FORMATTER.format(farmerCount)}
                />
                <PulsePill
                  label="Clients"
                  value={NUMBER_FORMATTER.format(clientCount)}
                />
                <PulsePill label="Ops squads" value="12" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/30 shadow-lg max-w-xs w-full">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <LineChart className="w-5 h-5" />
                  <span className="text-sm font-semibold">AI Outlook</span>
                </div>
                <span className="text-xs bg-black/30 px-2 py-1 rounded-full">
                  Live
                </span>
              </div>
              <div className="text-4xl font-bold leading-tight">
                {aiOutlookScore}%
              </div>
              <p className="text-xs text-white/80 mt-2">
                Blended confidence from collection velocity, sales pull-through,
                and on-time settlements.
              </p>
              <div className="flex items-center gap-2 text-xs text-white/90 mt-3">
                <ArrowUpRight className="w-4 h-4" />
                Keep margins sharp with balanced payouts vs receipts.
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            label="Active Farmers"
            value={NUMBER_FORMATTER.format(farmerCount)}
            color="from-emerald-100 via-white to-emerald-50"
            icon={<Users className="w-5 h-5 text-emerald-700" />}
            hint="Supply backbone, CompanyFarmers"
          />
          <SummaryCard
            label="Active Clients"
            value={NUMBER_FORMATTER.format(clientCount)}
            color="from-amber-100 via-white to-amber-50"
            icon={<Building2 className="w-5 h-5 text-amber-700" />}
            hint="CompanyClients heartbeat"
          />
          <SummaryCard
            label="Employees"
            value="89"
            color="from-blue-100 via-white to-blue-50"
            icon={<UserSquare className="w-5 h-5 text-blue-700" />}
            hint="CompanyEmployees coverage"
          />
          <SummaryCard
            label="Marketplace GMV"
            value="KES 18.2M"
            color="from-purple-100 via-white to-purple-50"
            icon={<ShoppingBag className="w-5 h-5 text-purple-700" />}
            hint="MarketPlace momentum"
          />
        </div>

        {/* AI Canvas & Analytics */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <p className="text-xs text-gray-500 font-semibold">AI Canvas</p>
                <h3 className="text-lg font-bold text-gray-900">
                  Collection vs sales trend
                </h3>
                <p className="text-sm text-gray-600">
                  Monthly look-back powered by Milk Journals & Client pull.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white"
                >
                  {MONTH_OPTIONS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
                  <Sparkles className="w-4 h-4" />
                  Time travel
                </div>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={CHART_MARGINS}>
                  <defs>
                    <linearGradient id="collectionFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="collection"
                    stroke="#10b981"
                    fillOpacity={1}
                    fill="url(#collectionFill)"
                    name="Collection (L)"
                  />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke="#6366f1"
                    fillOpacity={1}
                    fill="url(#salesFill)"
                    name="Sales (L)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <MiniStat label="Collected" value={`${(monthlyTotals.collection / 1000).toFixed(1)}k L`} />
              <MiniStat label="Sold" value={`${(monthlyTotals.sales / 1000).toFixed(1)}k L`} />
              <MiniStat label="Payments out" value={`KES ${(monthlyTotals.payout / 1000000).toFixed(1)}M`} />
              <MiniStat label="Payments in" value={`KES ${(monthlyTotals.receipts / 1000000).toFixed(1)}M`} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                <p className="text-sm font-semibold text-gray-800">AI signals</p>
              </div>
              <InsightRow
                title="Collection lift"
                value="Steady climb"
                helper={`${(monthlyTotals.collection / 1000).toFixed(1)}k L moving this month`}
              />
              <InsightRow
                title="Receipts vs payouts"
                value="Healthy spread"
                helper={`KES ${(monthlyTotals.receipts / 1000000).toFixed(1)}M in vs ${(monthlyTotals.payout / 1000000).toFixed(1)}M out`}
              />
              <InsightRow
                title="Market pull"
                value="Clients engaged"
                helper="Annotation boosts QC & trust for deliveries"
              />
            </div>

            <div className="bg-gradient-to-r from-slate-900 via-indigo-800 to-cyan-700 text-white rounded-2xl p-5 shadow-xl">
              <div className="flex items-center gap-2 mb-3">
                <LayoutGrid className="w-5 h-5" />
                <p className="text-sm font-semibold">Analytics preview</p>
              </div>
              <div className="space-y-3">
                {analyticsPreview.map((item) => (
                  <div key={item.label} className="bg-white/10 rounded-xl p-3">
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span>{item.label}</span>
                      <span>{item.value}</span>
                    </div>
                    <p className="text-xs text-white/80 mt-1">{item.detail}</p>
                    <div className="h-2 bg-white/10 rounded-full mt-2">
                      <div className={`h-full ${item.color} rounded-full ${item.fill}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Operations explorer */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Explore your company universe
              </h2>
              <p className="text-sm text-gray-600">
                Every card is a doorway — faster access to CompanyFarmers, Clients, Employees, Marketplace, Milk Journals, Annotation Place, Payments, and Reports.
              </p>
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              AI prioritized
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {operationsCards.map((card) => {
              const Icon = card.icon;
              return (
                <Link
                  key={card.title}
                  to={card.link}
                  className={`rounded-2xl p-4 shadow-lg bg-gradient-to-br ${card.accent} border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="p-2 bg-white rounded-full shadow-inner border border-gray-100">
                      <Icon className="w-5 h-5 text-gray-800" />
                    </div>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${card.badgeClass}`}
                    >
                      {card.change}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mt-3">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{card.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xl font-semibold text-gray-900">
                      {card.metric}
                    </p>
                    <ArrowUpRight className="w-4 h-4 text-gray-700" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </CompanyLayout>
  );
};

const SummaryCard = ({ label, value, color, icon, hint }) => (
  <div className={`rounded-2xl p-4 shadow-lg bg-gradient-to-br ${color} border border-gray-100`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs text-gray-600 font-semibold">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {hint ? <p className="text-xs text-gray-500 mt-1">{hint}</p> : null}
      </div>
      <div className="p-2 bg-white rounded-full shadow-inner border border-gray-100">
        {icon}
      </div>
    </div>
  </div>
);

const PulsePill = ({ label, value }) => (
  <div className="px-4 py-2 bg-white/15 backdrop-blur rounded-full text-sm font-semibold border border-white/30">
    <span className="text-white/80">{label}</span>{" "}
    <span className="text-white">{value}</span>
  </div>
);

const InsightRow = ({ title, value, helper }) => (
  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      <span className="text-xs text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full">
        {value}
      </span>
    </div>
    <p className="text-xs text-gray-600 mt-1">{helper}</p>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="bg-gray-50 rounded-xl border border-gray-100 p-3">
    <p className="text-xs font-semibold text-gray-600">{label}</p>
    <p className="text-lg font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

export default CompanyDashboard;
